import { jiraProjectKey } from "@/features/jira/api/jira-hooks";
import { linearProjectKey } from "@/features/linear/api/linear-hooks";
import { queryOptions } from "@tanstack/react-query";
import { crNotionReferencesRefetchInterval } from "../cr-notion-summary";
import { crLinkedDocsRefetchInterval } from "../cr-uploads";
import { changeRequestsApi } from "./change-requests-api";

export const changeRequestsQueries = {
  byId: (id: string) =>
    queryOptions({
      queryKey: ["change-requests", id],
      queryFn: () => changeRequestsApi.getChangeRequest(id),
    }),

  byProject: (projectId: string) =>
    queryOptions({
      queryKey: ["change-requests", "project", projectId],
      queryFn: () => changeRequestsApi.listChangeRequestsForProject(projectId),
    }),

  search: (query: string, projectId: string) =>
    queryOptions({
      queryKey: ["change-requests", "search", projectId, query],
      queryFn: () => changeRequestsApi.searchChangeRequests(query, projectId),
      enabled: query.length > 0,
    }),

  // CR-148: the dependency editor's typed-number lookup (exact, not capped).
  byNumber: (projectId: string, sequentialId: number) =>
    queryOptions({
      queryKey: ["change-requests", "number", projectId, sequentialId],
      queryFn: () => changeRequestsApi.findChangeRequestByNumber(projectId, sequentialId),
    }),

  events: (crId: string) =>
    queryOptions({
      queryKey: ["change-requests", crId, "events"],
      queryFn: () => changeRequestsApi.listCrEvents(crId),
    }),

  // CR-148: nested under the CR id, so a status / priority edit (which
  // invalidates byId) refreshes it for free; useCrEvents' realtime callback
  // invalidates it too, because an add, a removal and a dependency CR moving
  // to or from done (on its own page) each write a cr_events row on THIS CR,
  // while change_requests itself is not in realtime. Known gap: ARCHIVING a
  // dependency CR logs nothing here, so an open page shows it until refetch.
  dependencies: (crId: string) =>
    queryOptions({
      queryKey: ["change-requests", crId, "dependencies"],
      queryFn: () => changeRequestsApi.listCrDependencies(crId),
    }),

  // The two linked-container lists the CR detail page renders. They live here,
  // not as inline queries on the page, so each list and the mutations that
  // refresh it (change-requests-hooks) share one definition — do not re-inline
  // them. Note the consequence of nesting them under the CR id: `byId(crId)` is
  // a prefix of both, so a rename / status / priority edit marks them stale
  // too, exactly as it already does `events`.
  linkedChats: (crId: string) =>
    queryOptions({
      queryKey: ["change-requests", crId, "linked-chats"],
      queryFn: () => changeRequestsApi.listCrLinkedChats(crId),
    }),

  // CR-147: the list also holds uploaded files, which are parsed server-side
  // AFTER the upload RPC returns and have no realtime subscription — so the
  // query refreshes itself while a recent file is still being prepared. It
  // belongs here rather than at a call site so every consumer of the list (the
  // CR page today, a removal flow next) polls the same way.
  linkedDocs: (crId: string) =>
    queryOptions({
      queryKey: ["change-requests", crId, "linked-docs"],
      queryFn: () => changeRequestsApi.listCrLinkedDocuments(crId),
      refetchInterval: (query) => crLinkedDocsRefetchInterval(query.state.data),
    }),

  // CR-230: the CR's Notion page references. Nested under the CR id like the
  // lists above; cr_notion_references is not in realtime, so useCrEvents'
  // callback refreshes it (every add / update / removal logs a cr_events row).
  // CR-273 (D10): a summary refresh logs nothing, so the list also polls while
  // a freshly attached / updated page waits for its summary (bounded window).
  notionReferences: (crId: string) =>
    queryOptions({
      queryKey: ["change-requests", crId, "notion-references"],
      queryFn: () => changeRequestsApi.listCrNotionReferences(crId),
      refetchInterval: (query) => crNotionReferencesRefetchInterval(query.state.data),
    }),

  // CR-229: the CR's Jira issue links (direct read). Not in realtime: every
  // send / sync logs a cr_events row, so useCrEvents' callback refreshes it.
  jiraLinks: (crId: string) =>
    queryOptions({
      queryKey: ["change-requests", crId, "jira-links"],
      queryFn: () => changeRequestsApi.listCrJiraLinks(crId),
    }),
  /** CR-229: the status refresh through the api-server — outside the
   *  ["change-requests", crId] namespace, so neither a CR edit (byId) nor a
   *  cr_events invalidation re-asks Jira. */
  jiraLinksRefreshKey: (crId: string, projectId: string) =>
    [...jiraProjectKey(projectId), "links-refresh", crId] as const,

  // CR-239: the CR's Linear issue links (direct read). Not in realtime: every
  // send / sync logs a cr_events row, so useCrEvents' callback refreshes it.
  linearLinks: (crId: string) =>
    queryOptions({
      queryKey: ["change-requests", crId, "linear-links"],
      queryFn: () => changeRequestsApi.listCrLinearLinks(crId),
    }),
  /** CR-239: the state refresh through the api-server — outside the
   *  ["change-requests", crId] namespace, so neither a CR edit (byId) nor a
   *  cr_events invalidation re-asks Linear. */
  linearLinksRefreshKey: (crId: string, projectId: string) =>
    [...linearProjectKey(projectId), "links-refresh", crId] as const,
};
